import type { ReactNode } from 'react';

interface SettingRowProps {
  /** Uppercase label shown in the fixed-width left column. */
  label: string;
  /** The value in read mode, or the editor in edit mode. */
  children: ReactNode;
  /** Right-aligned control(s), e.g. a "Sửa" button. */
  action?: ReactNode;
}

/**
 * One full-width row of a settings card: a label, a value/editor, and an
 * optional right-aligned action. Rows are separated by a hairline bottom
 * border (no border on the last row) instead of a drop shadow, matching this
 * project's flat visual language.
 *
 * Stacks label above value on small screens so nothing gets squeezed on a
 * phone; from `sm:` up the label becomes a fixed-width left column.
 */
export function SettingRow({ label, children, action }: SettingRowProps) {
  return (
    <div className="flex flex-col gap-2 border-b border-surface-100 py-4 first:pt-0 last:border-b-0 last:pb-0 sm:flex-row sm:items-center sm:gap-4">
      <p className="text-xs font-medium uppercase tracking-wider text-surface-400 sm:w-40 sm:shrink-0">
        {label}
      </p>
      <div className="min-w-0 flex-1">{children}</div>
      {action && <div className="shrink-0 sm:ml-4">{action}</div>}
    </div>
  );
}
